import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Clock, Save, RefreshCw, Bell } from 'lucide-react';
import { adminApi } from '../../lib/api';
import { toast } from 'sonner';

export default function AdminLeadSettings({ adminSecret }) {
  const [followUpHours, setFollowUpHours] = useState(24);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, [adminSecret]);

  const fetchSettings = async () => {
    try {
      const response = await adminApi.getLeadFollowUpHours(adminSecret);
      setFollowUpHours(response.data.follow_up_hours || 24);
    } catch (error) {
      console.error('Failed to fetch lead settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const hours = parseInt(followUpHours, 10);
    if (isNaN(hours) || hours < 1 || hours > 168) {
      toast.error('Hours must be between 1 and 168');
      return;
    }

    setSaving(true);
    try {
      await adminApi.updateLeadFollowUpHours(hours, adminSecret);
      toast.success('Lead follow-up hours updated');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="rounded-sm bg-slate-800 border-slate-700">
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <RefreshCw className="h-5 w-5 animate-spin text-slate-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-sm bg-slate-800 border-slate-700">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Bell className="h-5 w-5 text-yellow-400" />
          Lead Follow-up Reminders
        </CardTitle>
        <CardDescription className="text-slate-400">
          Configure the follow-up window for new leads. Leads will be color-coded based on this setting.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Color Legend */}
        <div className="p-4 bg-slate-700/50 rounded-sm">
          <p className="text-sm text-slate-300 font-medium mb-3">Color Coding Legend:</p>
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 bg-yellow-500 rounded"></div>
              <span className="text-sm text-slate-300">Yellow - Lead is within follow-up window (new)</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 bg-red-500 rounded"></div>
              <span className="text-sm text-slate-300">Red - Lead is overdue for follow-up</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 bg-slate-500 rounded"></div>
              <span className="text-sm text-slate-300">No color - Lead has been qualified</span>
            </div>
          </div>
        </div>

        {/* Follow-up Hours Setting */}
        <div>
          <Label className="text-slate-300 mb-2 block flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Follow-up Window (Hours)
          </Label>
          <p className="text-sm text-slate-400 mb-3">
            New leads will turn from yellow to red after this many hours if not moved to "Qualified" stage.
          </p>
          <div className="flex gap-2">
            <Input
              type="number"
              min="1"
              max="168"
              value={followUpHours}
              onChange={(e) => setFollowUpHours(e.target.value)}
              placeholder="24"
              className="rounded-sm bg-slate-700 border-slate-600 text-white max-w-[120px]"
            />
            <span className="flex items-center text-slate-400">hours</span>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="rounded-sm bg-yellow-600 hover:bg-yellow-700 ml-auto"
            >
              {saving ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Example: 24 hours = 1 business day, 48 hours = 2 days
          </p>
        </div>

        {/* Notification Info */}
        <div className="border-t border-slate-700 pt-4">
          <p className="text-sm text-slate-400">
            <strong className="text-slate-300">Note:</strong> When a new lead is created, all team members with lead access will receive:
          </p>
          <ul className="text-sm text-slate-400 mt-2 list-disc list-inside space-y-1">
            <li>Email notification (if Google account is connected)</li>
            <li>Browser push notification (if enabled)</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
